/**
 * @module cercaliagl/layer/RainWeather
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import EventEmitter from 'eventemitter3';
/**
 * @enum {string}
 */
export var EventType = {
    FRAME_CHANGED: 'frameChanged',
};
/**
 * @const
 * @type {string}
 */
var LayerId = 'rain-weather-layer';
/**
 * @const
 * @type {string}
 */
var LayerUrl = 'https://api.rainviewer.com/public/weather-maps.json';
/**
 * @const
 * @type {string}
 */
var SourceId = 'rain-weather-source';
/**
 * @classdesc
 */
var RainWeather = /** @class */ (function (_super) {
    __extends(RainWeather, _super);
    /**
     * @constructor
     */
    function RainWeather() {
        var _this = _super.call(this) || this;
        /**
         * @private
         * @type {string}
         */
        _this.id_ = LayerId;
        /**
         * @private
         * @type {import("maplibre-gl").Map}
         */
        _this.map_ = null;
        /**
         * @private
         * @type {boolean}
         */
        _this.visible_ = true;
        /**
         * @private
         * @type {string}
         */
        _this.beforeLayerId_ = 'poi-level-4';
        /**
         * @private
         * @type {typeof setInterval}
         */
        _this.framesInterval = undefined;
        /**
         * @private
         * @type {typeof setInterval}
         */
        _this.opacityInterval = undefined;
        /**
         * @private
         * @type {Array<string>}
         */
        _this.currentFramesIdShown = [];
        return _this;
    }
    /**
     * @return {string} Id.
     */
    RainWeather.prototype.getLayerId = function () {
        return this.id_;
    };
    /**
     * @param {import("../Map").default} map MAp
     */
    RainWeather.prototype.setMap = function (map) {
        if (map) {
            this.map_ = map.getMapboxglMap();
            this.createLayer();
        }
        else {
            this.removeLayerData();
        }
    };
    /**
     * Creates a rain layer by fetching data from the specified LayerUrl and adding it to the map.
     * @private
     */
    RainWeather.prototype.createLayer = function () {
        var _this = this;
        fetch(LayerUrl)
            .then(function (res) { return res.json(); })
            .then(function (apiData) {
            var pastAndCurrentCast = __spreadArray(__spreadArray([], apiData.radar.past, true), apiData.radar.nowcast, true);
            _this.addRainLayer(apiData, pastAndCurrentCast);
            _this.manageFrames(pastAndCurrentCast);
        });
    };
    /**
     * Adds a rain layer to the map.
     * @param {Object} apiData - The API data object.
     * @param {Array} pastAndCurrentCast - An array of past and current weather forecast frames.
     * @private
     */
    RainWeather.prototype.addRainLayer = function (apiData, pastAndCurrentCast) {
        var _this = this;
        var currentBeforeLayerId = this.map_.getLayer(this.beforeLayerId_) ? this.beforeLayerId_ : null;
        pastAndCurrentCast.forEach(function (frame) {
            var currentSourceId = "".concat(SourceId, "_").concat(frame.path);
            var currentLayerId = "".concat(_this.id_, "_").concat(frame.path);
            _this.currentFramesIdShown.push(frame.path);
            _this.map_.addSource(currentSourceId, {
                type: 'raster',
                tiles: [
                    "".concat(apiData.host + frame.path, "/256/{z}/{x}/{y}/2/1_1.png"),
                ],
                tileSize: 256,
            });
            _this.map_.addLayer({
                id: currentLayerId,
                type: 'raster',
                source: currentSourceId,
                layout: { visibility: 'none' },
                minzoom: 0,
                maxzoom: 12,
            }, currentBeforeLayerId);
        });
    };
    /**
     * Manages the frames of the RainWeather layer.
     * @param {Array} pastAndCurrentCast - An array containing the past and current weather forecast frames.
     * @private
     */
    RainWeather.prototype.manageFrames = function (pastAndCurrentCast) {
        var _this = this;
        var i = 0;
        this.framesInterval = setInterval(function () {
            if (i > pastAndCurrentCast.length - 1) {
                i = 0;
            }
            // Reset opacity and visibility of all frames
            pastAndCurrentCast.forEach(function (frame) {
                _this.map_.setPaintProperty("".concat(_this.id_, "_").concat(frame.path), 'raster-opacity', 1);
                _this.map_.setLayoutProperty("".concat(_this.id_, "_").concat(frame.path), 'visibility', 'none');
            });
            // Show current and previous frame
            _this.map_.setLayoutProperty("".concat(_this.id_, "_").concat(pastAndCurrentCast[i].path), 'visibility', 'visible');
            _this.emit('frameChanged', pastAndCurrentCast[i].time);
            if (i - 1 >= 0) {
                _this.map_.setLayoutProperty("".concat(_this.id_, "_").concat(pastAndCurrentCast[i - 1].path), 'visibility', 'visible');
                var frame_1 = pastAndCurrentCast[i - 1];
                var opacity_1 = 1;
                if (_this.opacityInterval) {
                    clearInterval(_this.opacityInterval);
                }
                _this.opacityInterval = setInterval(function () {
                    _this.manageFrameOpacity(opacity_1, frame_1.path);
                    opacity_1 -= 0.1;
                }, 50);
            }
            i += 1;
        }, 1000);
    };
    /**
     * Manages the opacity of the frames.
     * @param {number} opacity - The opacity value.
     * @param {string} framePath - The frame path.
     * @private
    */
    RainWeather.prototype.manageFrameOpacity = function (opacity, framePath) {
        if (opacity <= 0) {
            clearInterval(this.opacityInterval);
            this.opacityInterval = null;
            this.map_.setLayoutProperty("".concat(this.id_, "_").concat(framePath), 'visibility', 'none'); // Hide previous frame when opacity turns 0
            return;
        }
        this.map_.setPaintProperty("".concat(this.id_, "_").concat(framePath), 'raster-opacity', opacity);
    };
    /**
     * Clear frames intervals
     * @private
    */
    RainWeather.prototype.clearIntervals = function () {
        if (this.framesInterval) {
            clearInterval(this.framesInterval);
        }
        if (this.opacityInterval) {
            clearInterval(this.opacityInterval);
        }
    };
    /**
     * Removes layer data from the map and clears intervals.
     * @private
     */
    RainWeather.prototype.removeLayerData = function () {
        var _this = this;
        this.clearIntervals();
        this.currentFramesIdShown.forEach(function (frameId) {
            _this.map_.removeLayer("".concat(_this.id_, "_").concat(frameId));
            _this.map_.removeSource("".concat(SourceId, "_").concat(frameId));
        });
        // this.map_.removeSource(SourceId);
        this.currentFramesIdShown = [];
    };
    /**
     * @return {Array.<Object.<string, any>>} Layers
     */
    RainWeather.prototype.getLayers = function () {
        return this.layers_;
    };
    return RainWeather;
}(EventEmitter));
export default RainWeather;
//# sourceMappingURL=RainWeather.js.map